import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, RefreshCw, CheckCircle2, AlertTriangle, XCircle, Clock, Mail, Activity } from 'lucide-react';
import { useGmailSyncHealth } from '../hooks/useGmailSyncHealth';

const COLABORADORES_EXPECTED = ['caroline', 'eduardo', 'fabio'];

function timeAgo(iso) {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffSec = Math.max(0, Math.floor((now - then) / 1000));
  if (diffSec < 60) return `há ${diffSec}s`;
  const min = Math.floor(diffSec / 60);
  if (min < 60) return `há ${min}min`;
  const h = Math.floor(min / 60);
  if (h < 48) return `há ${h}h`;
  const d = Math.floor(h / 24);
  return `há ${d}d`;
}

function fmtDuration(ms) {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60_000)}m ${Math.floor((ms % 60_000) / 1000)}s`;
}

function severityFor(latest) {
  if (!latest) return { color: 'bg-slate-300', label: 'sem dados', tone: 'slate', Icon: AlertTriangle };
  const age = Date.now() - new Date(latest.started_at).getTime();
  const hours = age / (60 * 60 * 1000);
  if (latest.status === 'error' || latest.status === 'timeout' || hours > 24) {
    return { color: 'bg-red-500', label: latest.status === 'error' ? 'erro' : latest.status === 'timeout' ? 'timeout' : 'atrasado (>24h)', tone: 'red', Icon: XCircle };
  }
  if (hours > 1) {
    return { color: 'bg-amber-500', label: 'ok (1-24h)', tone: 'amber', Icon: Clock };
  }
  return { color: 'bg-emerald-500', label: 'saudável (<1h)', tone: 'emerald', Icon: CheckCircle2 };
}

const StatusBadge = ({ status }) => {
  const map = {
    completed: { bg: 'bg-emerald-100 text-emerald-800', label: 'completed' },
    running: { bg: 'bg-blue-100 text-blue-800', label: 'running' },
    error: { bg: 'bg-red-100 text-red-800', label: 'error' },
    timeout: { bg: 'bg-amber-100 text-amber-800', label: 'timeout' },
  };
  const c = map[status] || { bg: 'bg-slate-100 text-slate-600', label: status || '—' };
  return <span className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${c.bg}`}>{c.label}</span>;
};

export default function GmailSyncHealthPage() {
  const { latestByColab, recentRuns, weeklyStats, loading, error, refetch } = useGmailSyncHealth();

  const byId = new Map(latestByColab.map((r) => [r.colaborador_id, r]));

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 px-6 py-3 flex items-center gap-4 sticky top-0 z-10">
        <Link to="/" className="text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1">
          <ArrowLeft size={14} /> Dashboard
        </Link>
        <div className="border-l border-slate-200 pl-4 flex-1">
          <h1 className="text-base font-semibold text-slate-800 flex items-center gap-2">
            <Activity size={16} className="text-amber-600" /> Saúde do Gmail Sync
          </h1>
          <p className="text-[11px] text-slate-500">Observabilidade das execuções do Apps Script dos 3 colaboradores</p>
        </div>
        <Link to="/ai-health/email-coverage" className="text-xs text-teal-700 hover:text-teal-900 flex items-center gap-1">
          <Mail size={12} /> Email Coverage →
        </Link>
        <button onClick={refetch} className="text-xs px-2 py-1 bg-slate-100 text-slate-700 rounded hover:bg-slate-200 flex items-center gap-1">
          <RefreshCw size={12} /> Atualizar
        </button>
      </header>

      <main className="p-6 max-w-6xl mx-auto space-y-4">
        {loading && <div className="p-8 text-center text-sm text-slate-500">Carregando...</div>}
        {error && !loading && (
          <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">{error}</div>
        )}

        {!loading && !error && (
          <>
            {/* Card 1 — Status por colaborador */}
            <section className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
              <h2 className="text-sm font-semibold text-slate-700 mb-3">Status por colaborador</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {COLABORADORES_EXPECTED.map((colab) => {
                  const latest = byId.get(colab);
                  const sev = severityFor(latest);
                  return (
                    <div key={colab} className="border border-slate-200 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-slate-800 capitalize">{colab}</span>
                        <span className={`w-2 h-2 rounded-full ${sev.color}`} title={sev.label} />
                      </div>
                      <div className="text-[11px] text-slate-500 mb-2 flex items-center gap-1">
                        <sev.Icon size={11} /> {sev.label}
                      </div>
                      {latest ? (
                        <>
                          <div className="text-xs text-slate-600">Último: <strong>{timeAgo(latest.started_at)}</strong> · <StatusBadge status={latest.status} /></div>
                          <div className="text-[11px] text-slate-500 mt-1">
                            <span className="tabular-nums">{latest.emails_fetched ?? 0}</span> fetched ·{' '}
                            <span className="tabular-nums">{latest.emails_inserted ?? 0}</span> inseridos ·{' '}
                            <span className="tabular-nums">{latest.duplicates_skipped ?? 0}</span> dup
                          </div>
                          <div className="text-[11px] text-slate-500">
                            {latest.run_type} · {fmtDuration(latest.duration_ms)}
                          </div>
                          {latest.status === 'error' && latest.error_message && (
                            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-[11px] text-red-700">
                              {latest.error_message}
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="text-[11px] text-slate-400 italic">Sem dados ainda (Apps Script ainda não envia)</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Card 2 — Histórico */}
            <section className="bg-white rounded-lg shadow-sm border border-slate-200">
              <div className="px-4 py-3 border-b border-slate-200">
                <h2 className="text-sm font-semibold text-slate-700">Histórico de runs (últimos 50)</h2>
              </div>
              <div className="overflow-auto">
                {recentRuns.length === 0 ? (
                  <div className="p-6 text-center text-sm text-slate-500">Nenhum run registrado ainda.</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase text-slate-500">
                      <tr>
                        <th className="px-3 py-2 text-left">Colaborador</th>
                        <th className="px-3 py-2 text-left">Tipo</th>
                        <th className="px-3 py-2 text-left">Status</th>
                        <th className="px-3 py-2 text-left">Início</th>
                        <th className="px-3 py-2 text-right">Duração</th>
                        <th className="px-3 py-2 text-right">Fetched</th>
                        <th className="px-3 py-2 text-right">Inseridos</th>
                        <th className="px-3 py-2 text-right">Dup</th>
                        <th className="px-3 py-2 text-left">Erro</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentRuns.map((r) => (
                        <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-3 py-1.5 text-xs text-slate-800 capitalize">{r.colaborador_id}</td>
                          <td className="px-3 py-1.5 text-xs text-slate-600">{r.run_type}</td>
                          <td className="px-3 py-1.5"><StatusBadge status={r.status} />{r.timeout_hit && <span className="ml-1 text-[10px] text-amber-700">⏱</span>}</td>
                          <td className="px-3 py-1.5 text-xs text-slate-500 whitespace-nowrap">{timeAgo(r.started_at)}</td>
                          <td className="px-3 py-1.5 text-xs text-slate-500 text-right">{fmtDuration(r.duration_ms)}</td>
                          <td className="px-3 py-1.5 text-xs text-right tabular-nums">{r.emails_fetched ?? 0}</td>
                          <td className="px-3 py-1.5 text-xs text-right tabular-nums">{r.emails_inserted ?? 0}</td>
                          <td className="px-3 py-1.5 text-xs text-right tabular-nums text-slate-500">{r.duplicates_skipped ?? 0}</td>
                          <td className="px-3 py-1.5 text-[11px] text-red-600 truncate max-w-[220px]" title={r.error_message || ''}>
                            {r.error_message || ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </section>

            {/* Card 3 — Métricas 7 dias */}
            <section className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
              <h2 className="text-sm font-semibold text-slate-700 mb-3">Métricas últimos 7 dias</h2>
              {weeklyStats.length === 0 ? (
                <div className="text-xs text-slate-500 italic">Sem dados nos últimos 7 dias.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="text-[11px] uppercase text-slate-500">
                    <tr>
                      <th className="px-2 py-1 text-left">Colaborador</th>
                      <th className="px-2 py-1 text-right">Runs</th>
                      <th className="px-2 py-1 text-right text-emerald-700">✓</th>
                      <th className="px-2 py-1 text-right text-red-700">⨯ erro</th>
                      <th className="px-2 py-1 text-right text-amber-700">⏱ timeout</th>
                      <th className="px-2 py-1 text-right">Emails inseridos</th>
                      <th className="px-2 py-1 text-right">Duração média</th>
                    </tr>
                  </thead>
                  <tbody>
                    {weeklyStats.map((s) => (
                      <tr key={s.colaborador_id} className="border-t border-slate-100">
                        <td className="px-2 py-1.5 text-xs capitalize">{s.colaborador_id}</td>
                        <td className="px-2 py-1.5 text-xs text-right tabular-nums">{s.total_runs}</td>
                        <td className="px-2 py-1.5 text-xs text-right tabular-nums text-emerald-700">{s.completed}</td>
                        <td className="px-2 py-1.5 text-xs text-right tabular-nums text-red-700">{s.errors}</td>
                        <td className="px-2 py-1.5 text-xs text-right tabular-nums text-amber-700">{s.timeouts}</td>
                        <td className="px-2 py-1.5 text-xs text-right tabular-nums">{s.total_inserted}</td>
                        <td className="px-2 py-1.5 text-xs text-right tabular-nums text-slate-500">{fmtDuration(s.avg_duration_ms)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
}
